import { useFrame, useThree } from '@react-three/fiber'
import { useEffect, useRef } from 'react'
import { Box3, Quaternion, Sphere, Vector3, type Object3D } from 'three'
import {
  DISTANCIA_MAXIMA,
  DISTANCIA_MINIMA,
  PREFIXO_SELECIONAVEL,
  alvoCamera,
  posicaoDaVista,
  type ComandoCamera,
  type VolumeEnquadramento,
} from './comandosCamera'

/** Duração da transição suave entre enquadramentos, em milissegundos. */
const DURACAO_TRANSICAO_MS = 450

interface ControlesOrbita {
  target?: Vector3
  update?: () => void
  addEventListener?: (tipo: string, ouvinte: () => void) => void
  removeEventListener?: (tipo: string, ouvinte: () => void) => void
}

interface Pose {
  posicao: Vector3
  alvo: Vector3
}

interface Transicao {
  origem: Pose
  destino: Pose
  inicioMs: number
}

const prefereMovimentoReduzido = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

/** Suavização ease-out: rápido no início, assenta no fim — sem ultrapassar o alvo. */
const suavizar = (t: number): number => 1 - Math.pow(1 - t, 3)

/**
 * Aplica os comandos de câmera dentro do Canvas.
 *
 * Vive aqui porque precisa de `useThree`/`useFrame`, mas não decide conteúdo
 * didático: quem aciona é a trilha de ferramentas, e o que cada comando
 * significa está em `comandosCamera.ts` (poses genéricas, testáveis sem
 * WebGL). Aqui só resolve o alvo em coordenadas de mundo e anima a transição.
 *
 * A transição é cancelável: começar a orbitar manualmente (evento `start` dos
 * controles) interrompe imediatamente, para o professor nunca "brigar" com uma
 * câmera em movimento. Com preferência por movimento reduzido, o enquadramento
 * troca na hora, sem animação.
 */
export function ControleCamera3D({
  comando,
  volume,
  distanciaMaxima = DISTANCIA_MAXIMA,
  distanciaMinima = DISTANCIA_MINIMA,
  aoInteragirManual = undefined,
}: {
  comando: ComandoCamera
  /** O que as vistas nomeadas enquadram. */
  volume: VolumeEnquadramento
  distanciaMaxima?: number
  distanciaMinima?: number
  /** Notifica o modo que iniciou o foco automático quando a pessoa assume a órbita. */
  aoInteragirManual?: () => void
}) {
  const camera = useThree((estado) => estado.camera)
  const cena = useThree((estado) => estado.scene)
  const controles = useThree((estado) => estado.controls) as ControlesOrbita | null

  useEffect(() => {
    camera.far = Math.max(500, Math.max(volume.comprimento, volume.largura, volume.altura) * 12)
    camera.updateProjectionMatrix()
  }, [camera, volume.comprimento, volume.largura, volume.altura])

  const transicaoRef = useRef<Transicao | null>(null)
  /** Enquadramento anterior ao último comando de vista/enquadramento, para "Voltar". */
  const anteriorRef = useRef<Pose | null>(null)

  // cancela a transição em andamento assim que o aluno/professor pega a órbita
  useEffect(() => {
    if (!controles?.addEventListener) return
    const cancelar = () => {
      transicaoRef.current = null
      aoInteragirManual?.()
    }
    controles.addEventListener('start', cancelar)
    return () => controles.removeEventListener?.('start', cancelar)
  }, [controles, aoInteragirManual])

  // Pose inicial já levando em conta a proporção da tela: o `Canvas` nasce com
  // uma posição fixa, que no celular em pé corta o objeto dos lados.
  useEffect(() => {
    if (!controles?.target) return
    const aspecto = (camera as { aspect?: number }).aspect ?? 1
    const posicao = posicaoDaVista('restaurar', volume, aspecto, distanciaMaxima)
    if (posicao) aplicarPose(camera, controles, { posicao: new Vector3(...posicao), alvo: new Vector3(...alvoCamera(volume)) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controles])

  useEffect(() => {
    const alvoAtual = controles?.target
    if (!alvoAtual || comando.id === 0) return

    const destino =
      comando.tipo === 'voltarEnquadramento'
        ? anteriorRef.current
        : resolverDestino({
          comando,
          cena,
          camera,
          alvoAtual,
          volume,
          distanciaMaxima,
          distanciaMinima,
        })
    if (!destino) return

    const origem: Pose = { posicao: camera.position.clone(), alvo: alvoAtual.clone() }

    // guarda o enquadramento anterior para "Voltar" — exceto o próprio "Voltar",
    // que não deve substituir a referência por onde a câmera estava só de passagem
    if (comando.tipo !== 'voltarEnquadramento') {
      anteriorRef.current = origem
    }

    if (prefereMovimentoReduzido()) {
      aplicarPose(camera, controles, destino)
      transicaoRef.current = null
      return
    }

    transicaoRef.current = { origem, destino, inicioMs: performance.now() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comando])

  useFrame(() => {
    const transicao = transicaoRef.current
    if (!transicao || !controles?.target) return

    const decorrido = performance.now() - transicao.inicioMs
    const t = Math.min(1, decorrido / DURACAO_TRANSICAO_MS)
    const f = suavizar(t)

    aplicarPose(camera, controles, {
      posicao: transicao.origem.posicao.clone().lerp(transicao.destino.posicao, f),
      alvo: transicao.origem.alvo.clone().lerp(transicao.destino.alvo, f),
    })

    if (t >= 1) transicaoRef.current = null
  })

  return null
}

function aplicarPose(
  camera: { position: Vector3; lookAt: (x: number, y: number, z: number) => void },
  controles: ControlesOrbita,
  pose: Pose,
): void {
  controles.target?.copy(pose.alvo)
  camera.position.copy(pose.posicao)
  camera.lookAt(pose.alvo.x, pose.alvo.y, pose.alvo.z)
  controles.update?.()
}

/** Resolve o destino (posição de câmera + alvo) de cada tipo de comando, em coordenadas de mundo. */
function resolverDestino(args: {
  comando: ComandoCamera
  cena: Object3D
  camera: CameraEnquadravel
  alvoAtual: Vector3
  volume: VolumeEnquadramento
  distanciaMaxima: number
  distanciaMinima: number
}): Pose | null {
  const { comando, cena, camera, alvoAtual, volume, distanciaMaxima, distanciaMinima } = args

  const vista = posicaoDaVista(comando.tipo, volume, camera.aspect ?? 1, distanciaMaxima)
  if (vista) {
    return { posicao: new Vector3(...vista), alvo: new Vector3(...alvoCamera(volume)) }
  }

  if (comando.tipo === 'liberarCamera') {
    const alvo = new Vector3(...alvoCamera(volume))
    const deslocamento = camera.position.clone().sub(alvo)
    if (deslocamento.lengthSq() === 0) {
      deslocamento.set(0.62, 0.43, 0.66)
    }
    const distancia = Math.max(12, Math.min(distanciaMaxima, deslocamento.length()))
    deslocamento.normalize().multiplyScalar(distancia)
    return { posicao: alvo.clone().add(deslocamento), alvo }
  }

  if (comando.tipo === 'enquadrarSelecao') {
    if (!comando.idAlvo) return null
    const nomeAlvo = `${PREFIXO_SELECIONAVEL}${comando.idAlvo}`
    const candidatos: Object3D[] = []
    cena.traverse((objeto) => {
      if (objeto.name === nomeAlvo) candidatos.push(objeto)
    })
    if (candidatos.length === 0) return null

    let objetoEscolhido = candidatos[0]!
    if (candidatos.length > 1) {
      let melhorPontuacao = -Infinity
      const posMundo = new Vector3()
      const direcaoCamera = new Vector3()
      const normalMundo = new Vector3()
      const quatMundo = new Quaternion()

      for (const obj of candidatos) {
        obj.getWorldPosition(posMundo)
        direcaoCamera.subVectors(camera.position, posMundo).normalize()
        obj.getWorldQuaternion(quatMundo)
        normalMundo.set(0, 0, 1).applyQuaternion(quatMundo).normalize()

        const dot = normalMundo.dot(direcaoCamera)
        const dist = camera.position.distanceTo(posMundo)
        const pontuacao = dot * 10 - dist * 0.1
        if (pontuacao > melhorPontuacao) {
          melhorPontuacao = pontuacao
          objetoEscolhido = obj
        }
      }
    }

    const caixa = new Box3().setFromObject(objetoEscolhido)
    if (caixa.isEmpty()) return null

    // Placas e rótulos são lidos com binóculo: visão direta de frente para facilitar a leitura
    const ehSinalizacao = comando.idAlvo.startsWith('painel') || comando.idAlvo.startsWith('rotulo')
    let direcaoDesejada: Vector3 | undefined
    if (ehSinalizacao) {
      const normalMundo = new Vector3(0, 0, 1)
        .applyQuaternion(objetoEscolhido.getWorldQuaternion(new Quaternion()))
        .normalize()
      direcaoDesejada = normalMundo.clone().add(new Vector3(0, 0.08, 0)).normalize()
    }

    return enquadrarCaixa(caixa, camera, alvoAtual, distanciaMaxima, distanciaMinima, direcaoDesejada)
  }

  if (comando.tipo === 'enquadrarCena') {
    const caixa = new Box3()
    cena.traverse((objeto) => {
      if (objeto.name.startsWith(PREFIXO_SELECIONAVEL)) caixa.expandByObject(objeto)
    })
    if (caixa.isEmpty()) return null
    return enquadrarCena(caixa, camera, alvoAtual, distanciaMaxima)
  }

  // aproximar / afastar: escala a distância mantendo a direção.
  const deslocamento = camera.position.clone().sub(alvoAtual)
  const distancia = deslocamento.length()
  if (!Number.isFinite(distancia) || distancia === 0) return null

  const fator = comando.tipo === 'aproximar' ? 0.78 : 1.28
  const proxima = Math.min(distanciaMaxima, Math.max(distanciaMinima, distancia * fator))
  deslocamento.multiplyScalar(proxima / distancia)
  return { posicao: alvoAtual.clone().add(deslocamento), alvo: alvoAtual.clone() }
}

type CameraEnquadravel = { position: Vector3; fov?: number; aspect?: number }


/**
 * A cena montada inteira no quadro. Diferente de enquadrar uma peça, a cena é
 * espalhada no chão e vista de três quartos: o maior lado da caixa não basta
 * (a diagonal escapava pelas bordas), então é a esfera que envolve a caixa que
 * precisa caber no campo mais estreito. O `maxDistance` dos controles de
 * órbita de cada módulo limita.
 */
function enquadrarCena(caixa: Box3, camera: CameraEnquadravel, alvoAtual: Vector3, distanciaMaxima: number): Pose {
  const esfera = caixa.getBoundingSphere(new Sphere())
  const fov = camera.fov ?? 42
  const proporcao = Math.min(1, camera.aspect ?? 1)
  const meioCampo = Math.atan(Math.tan((fov * Math.PI) / 360) * proporcao)
  const distancia = Math.min(distanciaMaxima, Math.max(DISTANCIA_MINIMA, esfera.radius / Math.sin(meioCampo)))
  return manterDirecao(esfera.center, distancia, camera, alvoAtual)
}

/** Mantém a direção de onde já se olhava: enquadrar não deve teletransportar o aluno para o outro lado da cena. */
function manterDirecao(centro: Vector3, distancia: number, camera: CameraEnquadravel, alvoAtual: Vector3): Pose {
  const direcao = camera.position.clone().sub(alvoAtual)
  if (direcao.lengthSq() === 0) direcao.set(-1, 0.7, 1)
  direcao.normalize().multiplyScalar(distancia)
  return { posicao: centro.clone().add(direcao), alvo: centro.clone() }
}

/** Distância e direção que põem a caixa inteira no quadro, mantendo o lado de onde já se olhava ou alinhando à face. */
function enquadrarCaixa(
  caixa: Box3,
  camera: CameraEnquadravel,
  alvoAtual: Vector3,
  distanciaMaxima: number,
  distanciaMinima = DISTANCIA_MINIMA,
  direcaoPreferencial?: Vector3,
): Pose {
  const centro = caixa.getCenter(new Vector3())
  const tamanho = caixa.getSize(new Vector3())
  const maiorLado = Math.max(tamanho.x, tamanho.y, tamanho.z)

  // Distância que põe o maior lado dentro do campo de visão, com folga para a
  // peça não encostar nas bordas. Com a tela em pé (celular), o campo
  // horizontal é o mais estreito e é ele que manda — só o vertical cortava a
  // peça dos lados.
  const fov = camera.fov ?? 42
  const proporcao = Math.min(1, camera.aspect ?? 1)
  const tangenteMeioCampo = Math.tan((fov * Math.PI) / 360) * proporcao
  const distanciaIdeal = (maiorLado * 1.6) / (2 * tangenteMeioCampo)
  const distancia = Math.min(distanciaMaxima, Math.max(distanciaMinima, distanciaIdeal))

  if (direcaoPreferencial && direcaoPreferencial.lengthSq() > 0) {
    const direcao = direcaoPreferencial.clone().normalize().multiplyScalar(distancia)
    return { posicao: centro.clone().add(direcao), alvo: centro.clone() }
  }

  return manterDirecao(centro, distancia, camera, alvoAtual)
}
